import { useTheme } from "@emotion/react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Slide,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { forwardRef, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";

const CreateNewConsumableDialog = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const { authToken, ezorder } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const createConsumable = async (description, uom, retailPrice) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.post(
        `/consumables`,
        {
          description,
          uom,
          retailPrice,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        // TODO: POST REQUEST
        // Call onSuccess
        onSuccess();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const formValidationSchema = Yup.object({
    description: Yup.string().required("description required"),
    uom: Yup.string().required("Unit of Measure Requried"),
    retailPrice: Yup.number()
      .positive("Please enter positive number")
      .required("Retail Price Required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: "",
      uom: "",
      retailPrice: 0,
    },
    validationSchema: formValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: (values) => {
      createConsumable(values.description, values.uom, values.retailPrice);
    },
  });

  return (
    <Dialog
      fullScreen={false}
      fullWidth={true}
      open={open}
      onClose={() => {
        if (!loading) handleClose(); // do not allow close when loading
      }}
    >
      <DialogTitle>Create New Consummable Item</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          sx={{ marginTop: "8px" }}
          label="Description"
          id="description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={
            formik.errors.description && `${formik.errors.description}`
          }
          onFocus={(e) => {
            e.target.select();
          }}
        />
        <TextField
          size="small"
          fullWidth
          sx={{ marginTop: "8px" }}
          label="Unit of Measure"
          id="uom"
          name="uom"
          value={formik.values.uom}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.uom && Boolean(formik.errors.uom)}
          helperText={formik.errors.uom && `${formik.errors.uom}`}
          onFocus={(e) => {
            e.target.select();
          }}
        />
        <TextField
          size="small"
          fullWidth
          sx={{ marginTop: "8px" }}
          label="Retail Price"
          id="retailPrice"
          name="retailPrice"
          value={formik.values.retailPrice}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.retailPrice && Boolean(formik.errors.retailPrice)
          }
          helperText={
            formik.errors.retailPrice && `${formik.errors.retailPrice}`
          }
          onFocus={(e) => {
            e.target.select();
          }}
        />
        <Box>
          {errorMessage && (
            <Box>
              <Typography color={"red"}>{errorMessage}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={() => {
            handleClose();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={loading}
          onClick={() => {
            formik.submitForm();
          }}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateNewConsumableDialog;
